// ============================================================
// AI Boardroom - Fase 1: centrale, task-type-agnostische change-queue.
//
// Dit is de ENIGE plek die de state machine kent:
//   pending -> approved | rejected
//   approved -> applied | failed
//   failed  -> applied  (retry na mislukking)
//
// De 'apply' zelf krijgt een executor-callback mee: in Fase 1 mapt die naar de
// interne Supabase-writes vanuit de app. In Fase 2 verhuist die executor naar
// een aparte backend-service die als enige de write-credentials bezit, zodat
// het propose-pad structureel read-only wordt. De interface hieronder blijft
// dan gelijk; alleen wie de executor draait verandert.
// ============================================================
import { supabase } from "./supabase";

const TABLE = "proposed_changes";

// Bekende waarden (documentatie/consistentie; de kolommen zijn bewust vrij tekst
// zodat nieuwe taaktypes/systemen geen migratie vereisen).
export const TASK_TYPES = ["content_draft", "crm_update", "outreach_email", "agenda_event", "research", "niche_score", "task", "launch_pakket", "generic"];
export const TARGET_SYSTEMS = ["documents", "tasks", "events", "leads", "launch_modules", "video", "web", "internal"];
export const STATUSES = ["pending", "approved", "rejected", "applied", "failed"];

// ── Propose: een agent legt een wijziging vast (nooit direct uitgevoerd) ──────
export async function proposeChange({
  sessionId = null, taskType, sourceAgent = null, targetSystem,
  payload = {}, reasoningLog = null, createdBy = null, idempotencyKey = null,
}) {
  if (!taskType) throw new Error("proposeChange: taskType is verplicht");
  if (!targetSystem) throw new Error("proposeChange: targetSystem is verplicht");
  const row = {
    session_id: sessionId, task_type: taskType, source_agent: sourceAgent,
    target_system: targetSystem, payload: payload || {}, reasoning_log: reasoningLog,
    status: "pending", created_by: createdBy, idempotency_key: idempotencyKey,
  };
  const { data, error } = await supabase.from(TABLE).insert([row]).select().single();
  // Dubbel voorstel (zelfde idempotency_key): geef de bestaande rij terug i.p.v. te falen.
  if (error && /duplicate key|idempotency/i.test(error.message) && idempotencyKey) {
    const { data: existing } = await supabase.from(TABLE).select("*").eq("idempotency_key", idempotencyKey).maybeSingle();
    if (existing) return existing;
  }
  if (error) throw new Error(error.message);
  return data;
}

// ── Lezen ─────────────────────────────────────────────────────────────────
export async function listProposals({ status = null, sessionId = null, limit = 200 } = {}) {
  let q = supabase.from(TABLE).select("*").order("created_at", { ascending: false }).limit(limit);
  if (status) q = Array.isArray(status) ? q.in("status", status) : q.eq("status", status);
  if (sessionId) q = q.eq("session_id", sessionId);
  const { data, error } = await q;
  if (error) throw new Error(error.message);
  return data || [];
}

// ── Approve / Reject (menselijke review) ────────────────────────────────────
export async function approveProposal(id, reviewer) {
  const { data, error } = await supabase.from(TABLE)
    .update({ status: "approved", reviewed_by: reviewer || null, reviewed_at: new Date().toISOString() })
    .eq("id", id).eq("status", "pending").select().single();
  if (error) throw new Error(error.message);
  if (!data) throw new Error("Voorstel niet meer 'pending' - mogelijk al beoordeeld.");
  return data;
}

export async function rejectProposal(id, reviewer, reason = null) {
  const { data, error } = await supabase.from(TABLE)
    .update({ status: "rejected", reviewed_by: reviewer || null, reviewed_at: new Date().toISOString(), error_log: reason || null, feedback: reason || null })
    .eq("id", id).eq("status", "pending").select().single();
  if (error) throw new Error(error.message);
  if (!data) throw new Error("Voorstel niet meer 'pending' - mogelijk al beoordeeld.");
  return data;
}

// ── Apply: de expliciete, aparte promotiestap ───────────────────────────────
// Voert de ECHTE wijziging uit via `executor(payload, row)` en zet daarna
// applied/failed. Idempotent: een al-toegepast voorstel wordt niet nog eens
// uitgevoerd. Alleen 'approved' (of 'failed' voor retry) mag hierlangs.
export async function applyProposal(id, executor) {
  const { data: row, error: readErr } = await supabase.from(TABLE).select("*").eq("id", id).single();
  if (readErr) throw new Error(readErr.message);
  if (!row) throw new Error("Voorstel niet gevonden.");
  if (row.status === "applied") return row;                 // idempotent: al gedaan
  if (row.status === "pending") throw new Error("Eerst goedkeuren voordat je toepast.");
  if (row.status === "rejected") throw new Error("Afgekeurd voorstel kan niet worden toegepast.");
  // status is 'approved' of 'failed' (retry)
  try {
    const result = await executor(row.payload || {}, row);
    const { data, error } = await supabase.from(TABLE)
      .update({ status: "applied", applied_at: new Date().toISOString(), error_log: null,
                result: typeof result === "string" ? { message: result } : (result || {}) })
      .eq("id", id).select().single();
    if (error) throw new Error(error.message);
    return data;
  } catch (e) {
    await supabase.from(TABLE).update({ status: "failed", error_log: String(e?.message || e) }).eq("id", id);
    throw e;
  }
}

// ── Kleine helper voor de UI: korte samenvatting per taaktype ───────────────
export function proposalHeadline(p) {
  const t = p?.task_type || "generic";
  const pl = p?.payload || {};
  if (t === "content_draft") return pl.title || "Concept/document";
  if (t === "crm_update") return (pl.name || pl.company || "CRM-lead");
  if (t === "outreach_email") return pl.subject || pl.title || "Outreach-mail";
  if (t === "agenda_event") return pl.title || "Afspraak";
  if (t === "research") return pl.query ? ("Research: " + pl.query) : "Web-research";
  if (t === "task") return pl.title || "Taak";
  return pl.title || t;
}
